<template>
  <SideMenu
    ref="sideMenu"
    :heading="$lang('product_listing', 'filter_heading')"
    :side="'left'"
    :has-fixed-footer="true"
    @on-side-menu-close="$emit('onFilterMenuClose')"
  >
    <div class="h-full">
      <div class="bg-main flex flex-1 flex-col overflow-auto h-full">
        <div
          v-for="section in sections"
          :key="'filter-section-' + section.key"
          class="p-24 border-b last:mb-208 last:border-0 empty:hidden"
        >
          <div
            v-if="headerTexts[section.key]"
          >
            <div class="pb-20">
              {{ headerTexts[section.key] }}
            </div>
            <div
              v-for="value in section.values"
              :key="'filter-value-' + value.label"
              class="pb-20"
            >
              <div v-if="section.isColorPicker && value.label.toString() != '-'">
                <div class="flex w-full relative">
                  <div
                    class="
                    flex flex-1
                    items-center
                    relative
                    pl-32
                    cursor-pointer
                    select-none
                    text-price
                    z-30
                    pt-4
                  "
                    @click="onCheckFilter(section.key, value.label)"
                  >
                    {{ value.label.toString() }}
                    <div
                      class="
                      absolute
                      top-0
                      left-0
                      h-24
                      w-24
                      bg-white
                      border border-default
                      rounded-regular
                    "
                      :style="`background-color: ${value.hex}`"
                    />
                  </div>
                  <div
                    class="absolute -m-8 inset-0 rounded-regular"
                    :class="{
                      'bg-picker': !!(
                        activeFilters[section.key] &&
                        activeFilters[section.key].includes(value.label)
                      ),
                    }"
                  />
                  <div
                    v-if="
                      !!(
                        activeFilters[section.key] &&
                        activeFilters[section.key].includes(value.label)
                      )
                    "
                    class="absolute right-8"
                  >
                    <fa class="text-primary" :icon="['fal', 'times']" />
                  </div>
                </div>
              </div>
              <CheckboxComponent
                v-else-if="value.label.toString() != '-'"
                :text="value.label.toString()"
                :checked="
                  !!(
                    activeFilters[section.key] &&
                    activeFilters[section.key].includes(value.label)
                  )
                "
                :highlight="true"
                :disabled="value.isLocked"
                @on-check="onCheckFilter(section.key, value.label, value.isLocked)"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="
          absolute
          bg-picker
          flex
          pt-24
          pr-24
          pl-24
          pb-32
          right-0
          bottom-0
          left-0
          z-30
        "
      >
        <ButtonComponent
          class="mr-4"
          :text="'Rensa'"
          :is-danger-btn="true"
          :is-block="true"
          @click="$emit('clearFilter')"
        />
        <ButtonComponent
          class="flex-1 ml-4"
          :text="`${$lang(
            'product_listing',
            'finish_filter_btn'
          )} (${amountofproducts})`"
          :is-block="true"
          :is-secondary-btn="true"
          @click="closeSideMenu"
        />
      </div>
    </div>
  </SideMenu>
</template>

<script lang="ts">

import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import CheckboxComponent from '~/components/forms/CheckboxComponent.vue';
import SideMenu, { type SideMenuInterface } from '~/components/navigation/SideMenu.vue';
import SortOrder from '~/constants/sort-order-sizes';
import { defineComponent } from 'vue';

type FilterSection = {
  key: string;
  values: {
    label: string;
    hex?: string;
  }[];
  isColorPicker?: undefined;
};
export default defineComponent({
  components: {
    SideMenu,
    CheckboxComponent,
    ButtonComponent,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    activeFilters: {
      type: Object,
      required: true,
    },
    lockedFilters: {
      type: Object,
      required: true,
    },
    amountofproducts: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      headerTexts: {
        'items.name': this.$lang('product_listing', 'size_filter'),
        pr_news_value: this.$lang('product_listing', 'news_filter'),
        pr_staff_favorite_value: this.$lang(
          'product_listing',
          'favorite_filter'
        ),
        'var_sh_swatch.desc': this.$lang('product_listing', 'color_filter'),
        'pr_washing.name': this.$lang('product_listing', 'washing_filter'),
        pr_material: this.$lang('product_listing', 'material_filter'),
        'pr_seal.name': this.$lang('product_listing', 'seal_filter'),
        pr_gender: this.$lang('product_listing', 'gender_filter'),
        pr_type: this.$lang('product_listing', 'type_filter'),
      },
    };
  },
  computed: {
    sections(): FilterSection[] {
      const sections = Object.entries(this.filters).map((entry) => {
        const key = entry[0];
        const values = entry[1] as any[];

        if (key === 'pr_news_value' || key === 'pr_staff_favorite_value') {
          return {
            key,
            values: [
              {
                label: this.$lang('general', 'yes'),
                isLocked:
                  this.lockedFilters[key] &&
                  this.lockedFilters[key][0] === values[0],
              },
            ],
          };
        } else if (key === 'items.name') {
          return {
            key,
            values: values
              .sort((a, z) => {
                const aSize = a.toLowerCase();
                const bSize = z.toLowerCase();
                return SortOrder.indexOf(aSize) - SortOrder.indexOf(bSize);
              })
              .map((value) => ({
                label: value,
                isLocked:
                  this.lockedFilters[key] &&
                  this.lockedFilters[key].includes(value),
              })),
          };
        } else if (key === 'var_sh_swatch.desc') {
          const subAttribute = key.split('.')[1];
          return {
            key,
            values: values.map((value) => ({
              label: value[subAttribute],
              hex: value.hex,
              isLocked:
                this.lockedFilters[key] &&
                this.lockedFilters[key].includes(value),
            })),
            isColorPicker: true,
          };
        } else {
          const subAttribute = key.split('.')[1];
          const formattedValues = subAttribute
            ? values.map((value) => ({
              label: value[subAttribute] || value,
              isLocked:
                  this.lockedFilters[key] &&
                  this.lockedFilters[key].includes(value),
            }))
            : values.map((value) => ({
              label: value,
              isLocked:
                  this.lockedFilters[key] &&
                  this.lockedFilters[key].includes(value),
            }));

          return {
            key,
            values: formattedValues,
          };
        }
      }) as FilterSection[];

      return sections
        .filter((section) => section.key !== 'categories')
        .reduce((acc, element) => {
          if (
            element.key === 'pr_gender' ||
            (element.key === 'pr_type' && acc[0].key !== 'pr_gender')
          ) {
            return [element, ...acc];
          } else if (element.key === 'pr_type' && acc[0].key === 'pr_gender') {
            const slicedArr = acc.slice(1);
            return [acc[0], element, ...slicedArr];
          }
          return [...acc, element];
        }, [] as FilterSection[]);
    },
  },
  mounted() {
    console.log(this.sections);
    this.toggleSideMenu();
  },
  methods: {
    closeSideMenu() {
      const sideMenu = this.$refs.sideMenu as SideMenuInterface;
      sideMenu.closeOverlay();
    },
    onCheckFilter(field: string, value: string, isLocked?: boolean) {
      if (!isLocked) {
        this.$emit('onToggleFilter', field, value);
      }
    },
    toggleSideMenu() {
      const sideMenu = this.$refs.sideMenu as SideMenuInterface;
      sideMenu.openOverlay();
    },
  },
});
</script>
